import { addDays, startOfDay } from 'date-fns';
import type { ScheduleGanttScalePreset } from './scheduleGanttTimeline';

/**
 * Horizontal position (px) of "today" within the timeline scale strip, matching the same
 * start→end range as the Gantt `start` / `end` props (so it aligns with task bars).
 *
 * Previously, position was inferred from the first/last **visible** scale labels (e.g. week columns),
 * which span a shorter calendar range than `start`/`end` and skewed the blue line vs. bars.
 */
export function computeTodayMarkerOffsetInScalePx(options: {
  scaleWidth: number;
  rangeStart: Date;
  rangeEnd: Date;
  scalePreset: ScheduleGanttScalePreset;
  /** Bottom (finest) scale row cells — used only for `months` preset intra-month positioning. */
  cells: HTMLElement[];
  /** Defaults to today. */
  today?: Date;
}): number {
  const today = startOfDay(options.today ?? new Date());
  const { scaleWidth, rangeStart, rangeEnd, scalePreset, cells } = options;
  const startMs = startOfDay(rangeStart).getTime();
  const endExclusiveMs = startOfDay(addDays(rangeEnd, 1)).getTime();
  const nowMs = today.getTime();

  if (scalePreset === 'months' && cells.length > 0) {
    const todayMonthLong = today.toLocaleString(undefined, { month: 'long' }).toLowerCase();
    const todayMonthShort = today.toLocaleString(undefined, { month: 'short' }).toLowerCase();
    const monthIndex = cells.findIndex((cell) => {
      const text = (cell.textContent || '').trim().toLowerCase();
      return text.includes(todayMonthLong) || text.includes(todayMonthShort);
    });
    if (monthIndex >= 0) {
      let offset = 0;
      for (let i = 0; i < monthIndex; i += 1) {
        offset += cells[i].getBoundingClientRect().width;
      }
      const cellW = cells[monthIndex].getBoundingClientRect().width || scaleWidth / cells.length;
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const dayProgress = (today.getDate() - 1) / Math.max(1, daysInMonth);
      return offset + dayProgress * cellW;
    }
  }

  const clamped = Math.min(Math.max(nowMs, startMs), endExclusiveMs);
  const progress = endExclusiveMs > startMs ? (clamped - startMs) / (endExclusiveMs - startMs) : 0;
  return progress * scaleWidth;
}

/** SVAR default compresses the scale to fit the viewport; disable so `cellWidth` drives scroll. */
export const SCHEDULE_GANTT_TIMELINE_CELL_WIDTH = 80;
